import {
  clientAccessConfig,
  type OperationId,
  realmAccessConfig,
} from "@gen/app-access-config";
import { setAxiosFactory } from "@gen/app-query";
import { MutationCache, QueryClient } from "@tanstack/react-query";
import axios from "axios";
import { enqueueSnackbar } from "notistack";
import {
  type User,
  UserManager,
  type UserManagerSettings,
  WebStorageStateStore,
} from "oidc-client-ts";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { localStorage } from "~/utilities/storage";

import {
  getActiveClientIdStorageKey,
  getClientRolesByClientId,
  getUserRoles,
  hasAnyRole,
  resolveActiveClientId,
} from "./helpers";

const AUTH_BROADCAST_CHANNEL_NAME = "auth-events";
const LOGOUT_MESSAGE_TYPE = "logout";

function getCleanRedirectUri(): string {
  const url = new URL(window.location.href);
  ["code", "state", "session_state", "iss"].forEach((p) =>
    url.searchParams.delete(p),
  );
  return url.toString();
}

type AuthStatus =
  | "loading"
  | "authenticated"
  | "unauthorized"
  | "unauthenticated";

function createCanExecuteOperationFn(
  status: AuthStatus,
  activeClientId: string | null,
  canExecuteAdminOperation: (operationId: OperationId) => boolean,
  clientOperationsByClientId: Map<string, Set<OperationId>>,
): (operationId: OperationId) => boolean {
  return (operationId: OperationId): boolean => {
    if (status === "unauthorized") {
      return false;
    }
    if (canExecuteAdminOperation(operationId)) {
      return true;
    }
    if (!activeClientId) {
      return false;
    }
    const operations = clientOperationsByClientId.get(activeClientId);
    if (!operations) {
      return false;
    }
    return operations.has(operationId);
  };
}

export interface AuthStatusCtx {
  status: AuthStatus;
  user: User | null;
  logout: () => void;
}

export interface AuthOperationsCtx {
  canExecuteAdminOperation: (operationId: OperationId) => boolean;
  canExecuteAnyAdminOperation: () => boolean;
  availableAdminOperations: Set<OperationId>;
  clientOperationsByClientId: Map<string, Set<OperationId>>;
}

export interface ActiveClientCtx {
  activeClientId: string | null;
  setActiveClientId: (clientId: string | null) => void;
  accessibleClientIds: string[];
  resolveActiveClientId: (preferredClientId?: string | null) => string | null;
}

export interface QueryClientCtx {
  queryClient: QueryClient;
}

export const AuthStatusContext = createContext<AuthStatusCtx>({
  status: "unauthenticated",
  user: null,
  logout: () => {},
});
AuthStatusContext.displayName = "AuthStatusContext";

export const AuthOperationsContext = createContext<AuthOperationsCtx>({
  canExecuteAdminOperation: () => false,
  canExecuteAnyAdminOperation: () => false,
  availableAdminOperations: new Set<OperationId>(),
  clientOperationsByClientId: new Map(),
});
AuthOperationsContext.displayName = "AuthOperationsContext";

export const ActiveClientContext = createContext<ActiveClientCtx>({
  activeClientId: null,
  setActiveClientId: () => {},
  accessibleClientIds: [],
  resolveActiveClientId: () => null,
});
ActiveClientContext.displayName = "ActiveClientContext";

export const QueryClientContext = createContext<QueryClientCtx>(null!);
QueryClientContext.displayName = "QueryClientContext";

export type AuthCtx =
  | {
      status: "loading";
      user: null;
      logout: () => void;
      queryClient: QueryClient;
      canExecuteAdminOperation: (operationId: OperationId) => boolean;
      canExecuteOperation: (operationId: OperationId) => boolean;
      canExecuteAnyAdminOperation: () => boolean;
      activeClientId: string | null;
      setActiveClientId: (clientId: string | null) => void;
      resolveActiveClientId: (
        preferredClientId?: string | null,
      ) => string | null;
      accessibleClientIds: string[];
    }
  | {
      status: "authenticated";
      user: User;
      logout: () => void;
      queryClient: QueryClient;
      canExecuteAdminOperation: (operationId: OperationId) => boolean;
      canExecuteOperation: (operationId: OperationId) => boolean;
      canExecuteAnyAdminOperation: () => boolean;
      activeClientId: string | null;
      setActiveClientId: (clientId: string | null) => void;
      resolveActiveClientId: (
        preferredClientId?: string | null,
      ) => string | null;
      accessibleClientIds: string[];
    }
  | {
      status: "unauthorized";
      user: User;
      logout: () => void;
      queryClient: QueryClient;
      canExecuteAdminOperation: (operationId: OperationId) => boolean;
      canExecuteOperation: (operationId: OperationId) => boolean;
      canExecuteAnyAdminOperation: () => boolean;
      activeClientId: string | null;
      setActiveClientId: (clientId: string | null) => void;
      resolveActiveClientId: (
        preferredClientId?: string | null,
      ) => string | null;
      accessibleClientIds: string[];
    }
  | {
      status: "unauthenticated";
      user: null;
      logout: () => void;
      queryClient: QueryClient;
      canExecuteAdminOperation: (operationId: OperationId) => boolean;
      canExecuteOperation: (operationId: OperationId) => boolean;
      canExecuteAnyAdminOperation: () => boolean;
      activeClientId: string | null;
      setActiveClientId: (clientId: string | null) => void;
      resolveActiveClientId: (
        preferredClientId?: string | null,
      ) => string | null;
      accessibleClientIds: string[];
    };

export const AuthContext = createContext<AuthCtx>(null!);
AuthContext.displayName = "AuthContext";

export interface AuthQueryFactoryResult {
  authStatusValue: AuthStatusCtx;
  authOperationsValue: AuthOperationsCtx;
  activeClientValue: ActiveClientCtx;
  queryClientValue: QueryClientCtx;
  authValue: AuthCtx;
}

interface AuthQueryConfig {
  auth: {
    host: string;
    realm: string;
    clientId: string;
    redirectUri: string;
    responseType: string;
    scope: string;
    automaticSilentRenew: boolean;
    accessTokenExpiringNotificationTimeInSeconds: number;
    realmRequiredRoles: string[];
    clientRequiredRoles: string[];
    ignoredClients: string[];
  };
  api: {
    baseUrl: string;
  };
}

export function useAuthQueryFactory(config: AuthQueryConfig): AuthCtx {
  const result = useAuthQueryFactoryInternal(config);
  return result.authValue;
}

export function useAuthQueryFactoryInternal(
  config: AuthQueryConfig,
): AuthQueryFactoryResult {
  const [user, setUser] = useState<User | null | undefined>(null);
  const [status, setStatus] = useState<AuthStatus>("unauthenticated");
  const [activeClientId, setActiveClientIdState] = useState<string | null>(
    null,
  );

  const realmRequiredRoles = useMemo(
    () => new Set(config.auth.realmRequiredRoles),
    [config.auth.realmRequiredRoles],
  );
  const clientRequiredRoles = useMemo(
    () => new Set(config.auth.clientRequiredRoles),
    [config.auth.clientRequiredRoles],
  );

  const userManagerSettings: UserManagerSettings = useMemo(
    () => ({
      authority: config.auth.host + "/auth/realms/" + config.auth.realm,
      client_id: config.auth.clientId,
      redirect_uri: config.auth.redirectUri,
      response_type: config.auth.responseType,
      scope: config.auth.scope,
      automaticSilentRenew: config.auth.automaticSilentRenew,
      accessTokenExpiringNotificationTimeInSeconds:
        config.auth.accessTokenExpiringNotificationTimeInSeconds,
      userStore: new WebStorageStateStore({ store: window.sessionStorage }),
      stateStore: new WebStorageStateStore({ store: window.sessionStorage }),
    }),
    [
      config.auth.host,
      config.auth.realm,
      config.auth.clientId,
      config.auth.redirectUri,
      config.auth.responseType,
      config.auth.scope,
      config.auth.automaticSilentRenew,
      config.auth.accessTokenExpiringNotificationTimeInSeconds,
    ],
  );

  const userManager = useMemo(() => {
    return new UserManager(userManagerSettings);
  }, [userManagerSettings]);

  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: (failureCount, error) => {
              if (error.status && error.status >= 500 && error.status < 600) {
                return failureCount < 3;
              }
              return false;
            },
            staleTime: 5 * 60 * 1000,
          },
        },
        mutationCache: new MutationCache({
          onError: (error, _variables, _context, mutation) => {
            if (mutation.meta?.suppressError) {
              return;
            }
            if (
              error.status &&
              error.status >= 400 &&
              error.status < 500 &&
              error.response?.message
            ) {
              enqueueSnackbar(error.response.message, {
                variant: "error",
              });
            } else {
              console.error(error);
            }
          },
          onSuccess(_data, _variables, _context, mutation) {
            const meta = mutation.meta;
            if (meta?.suppressSuccess) {
              return;
            }
            if (meta?.success) {
              enqueueSnackbar(meta.success, { variant: "success" });
            }
          },
        }),
      }),
    [],
  );

  const axiosInstance = useMemo(() => {
    const instance = axios.create({
      baseURL: config.api.baseUrl,
      timeout: 40_000,
    });
    instance.interceptors.request.use(async (request) => {
      const current = await userManager.getUser();
      if (current?.access_token && !current.expired) {
        request.headers.Authorization = `Bearer ${current.access_token}`;
      }
      return request;
    });
    return instance;
  }, [config.api.baseUrl, userManager]);

  useEffect(() => {
    setAxiosFactory(() => axiosInstance);

    return () => setAxiosFactory(undefined!);
  }, [axiosInstance]);

  const didInitialize = useRef(false);

  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);
  if (!broadcastChannelRef.current) {
    broadcastChannelRef.current = new BroadcastChannel(
      AUTH_BROADCAST_CHANNEL_NAME,
    );
  }

  const performLogout = useCallback(
    async (isFromBroadcast = false) => {
      setUser(null);
      setStatus("unauthenticated");
      queryClient.clear();

      try {
        if (isFromBroadcast) {
          await userManager.signoutSilent();
        } else {
          await userManager.signoutRedirect({
            post_logout_redirect_uri: getCleanRedirectUri(),
          });
        }
      } catch (error) {
        console.warn("Signout failed:", error);
      }

      await userManager.removeUser();
    },
    [userManager, queryClient],
  );

  useEffect(() => {
    const channel = broadcastChannelRef.current;
    if (!channel) {
      return;
    }

    const handleLogoutBroadcast = (event: MessageEvent) => {
      if (event.data.type === LOGOUT_MESSAGE_TYPE) {
        performLogout(true);
      }
    };

    channel.addEventListener("message", handleLogoutBroadcast);

    return () => {
      channel.removeEventListener("message", handleLogoutBroadcast);
    };
  }, [performLogout]);

  useEffect(() => {
    const channel = broadcastChannelRef.current;
    return () => {
      if (channel) {
        channel.close();
        broadcastChannelRef.current = null;
      }
    };
  }, []);

  const logout = useCallback(async () => {
    try {
      broadcastChannelRef.current?.postMessage({ type: LOGOUT_MESSAGE_TYPE });
    } catch {
      // Channel may be closed, ignore.
    }

    await performLogout(false);
  }, [performLogout]);

  const availableAdminOperations = useMemo(() => {
    const operationsSet = new Set<OperationId>();
    if (!user || typeof user.access_token !== "string") {
      return operationsSet;
    }
    try {
      const optionalRoles = getUserRoles(user);
      if (optionalRoles === null) {
        return operationsSet;
      }
      for (const role of optionalRoles) {
        const ops = realmAccessConfig[role] ?? [];
        for (const op of ops) {
          operationsSet.add(op);
        }
      }
      return operationsSet;
    } catch (error) {
      console.error("Failed to extract user roles for operations:", error);
      return operationsSet;
    }
  }, [user]);

  const clientRolesByClientId = useMemo(() => {
    return getClientRolesByClientId(user, config.auth.ignoredClients);
  }, [user, config.auth.ignoredClients]);

  const clientOperationsByClientId = useMemo(() => {
    const operationsByClient = new Map<string, Set<OperationId>>();

    if (!user || typeof user.access_token !== "string") {
      return operationsByClient;
    }

    try {
      for (const [clientId, roles] of clientRolesByClientId.entries()) {
        if (roles.size === 0) {
          continue;
        }

        let clientOperations = operationsByClient.get(clientId);
        if (!clientOperations) {
          clientOperations = new Set<OperationId>();
          operationsByClient.set(clientId, clientOperations);
        }

        for (const role of roles.values()) {
          const operations = clientAccessConfig[role] ?? [];
          for (const operation of operations) {
            clientOperations.add(operation);
          }
        }

        if (clientOperations.size === 0) {
          operationsByClient.delete(clientId);
        }
      }

      return operationsByClient;
    } catch (error) {
      console.error("Failed to extract client roles for operations:", error);
      return operationsByClient;
    }
  }, [user, clientRolesByClientId]);

  const canExecuteAdminOperation = useCallback(
    (operationId: OperationId) => {
      return availableAdminOperations.has(operationId);
    },
    [availableAdminOperations],
  );

  const canExecuteAnyAdminOperation = useCallback(() => {
    return availableAdminOperations.size > 0;
  }, [availableAdminOperations]);

  const canExecuteOperationForActiveClient = useMemo(
    () =>
      createCanExecuteOperationFn(
        status,
        activeClientId,
        canExecuteAdminOperation,
        clientOperationsByClientId,
      ),
    [
      activeClientId,
      canExecuteAdminOperation,
      clientOperationsByClientId,
      status,
    ],
  );

  const setActiveClientId = useCallback(
    (clientId: string | null) => {
      setActiveClientIdState(clientId);
      if (user && !canExecuteAnyAdminOperation()) {
        try {
          const storageKey = getActiveClientIdStorageKey(user);
          if (clientId) {
            localStorage.setItem(storageKey, clientId);
          } else {
            localStorage.removeItem(storageKey);
          }
        } catch (error) {
          console.error(
            "Couldn't update active clientId in local storage:",
            error,
          );
        }
      }
    },
    [canExecuteAnyAdminOperation, user],
  );

  useEffect(() => {
    if (!userManager || didInitialize.current) {
      return;
    }
    didInitialize.current = true;

    void (async (): Promise<void> => {
      try {
        const url = new URL(window.location.href);
        const params = url.searchParams;

        if (params.has("code")) {
          setStatus("loading");
          await userManager.signinCallback();

          ["code", "state", "session_state", "iss"].forEach((p) => {
            params.delete(p);
          });
          window.history.replaceState(
            window.history.state,
            document.title,
            url.pathname +
              (url.search ? "?" + url.searchParams.toString() : "") +
              url.hash,
          );
          return;
        }

        const stored = await userManager.getUser();
        if (stored && !stored.expired) {
          try {
            let preferredClientId = null;
            const pathSegments = window.location.pathname.split("/");
            if (pathSegments[1] === "clients" && pathSegments[2]) {
              preferredClientId = pathSegments[2];
            }

            const initialClientId = resolveActiveClientId(
              stored,
              clientRequiredRoles,
              realmRequiredRoles,
              config.auth.ignoredClients,
              preferredClientId,
            );

            if (
              !hasAnyRole(
                stored,
                realmRequiredRoles,
                clientRequiredRoles,
                config.auth.ignoredClients,
                initialClientId,
              )
            ) {
              setUser(stored);
              setStatus("unauthorized");
              return;
            }
            setUser(stored);
            setStatus("authenticated");
            setActiveClientId(initialClientId);
            return;
          } catch (error) {
            console.error("Failed to validate user roles:", error);
            setUser(stored);
            setStatus("unauthorized");
            return;
          }
        }

        await userManager.signinRedirect({
          redirect_uri: getCleanRedirectUri(),
        });
      } catch (error) {
        console.error("Couldn't sign in:", error);
        setStatus("unauthenticated");
        await userManager.signinRedirect({
          redirect_uri: getCleanRedirectUri(),
        });
      }
    })();
  }, [
    userManager,
    realmRequiredRoles,
    clientRequiredRoles,
    setActiveClientId,
    config.auth.ignoredClients,
  ]);

  useEffect(() => {
    const onLoad = (u: User): void => {
      try {
        let preferredClientId = null;
        const pathSegments = window.location.pathname.split("/");
        if (pathSegments[1] === "clients" && pathSegments[2]) {
          preferredClientId = pathSegments[2];
        }

        const initialClientId = resolveActiveClientId(
          u,
          clientRequiredRoles,
          realmRequiredRoles,
          config.auth.ignoredClients,
          preferredClientId,
        );

        if (
          !hasAnyRole(
            u,
            realmRequiredRoles,
            clientRequiredRoles,
            config.auth.ignoredClients,
            initialClientId,
          )
        ) {
          setUser(u);
          setStatus("unauthorized");
          return;
        }
        setUser(u);
        setStatus("authenticated");
      } catch (error) {
        console.error("Failed to validate user roles:", error);
        setUser(u);
        setStatus("unauthorized");
      }
    };
    const onLogout = (): void => {
      setUser(null);
      setStatus("unauthenticated");
      queryClient.clear();
    };

    const onSilentRenewFail = async (): Promise<void> => {
      setUser(null);
      setStatus("unauthenticated");
      queryClient.clear();
      await userManager.removeUser();
      userManager.signinRedirect({
        redirect_uri: getCleanRedirectUri(),
      });
    };

    userManager.events.addUserLoaded(onLoad);
    userManager.events.addUserUnloaded(onLogout);
    userManager.events.addSilentRenewError(onSilentRenewFail);

    return () => {
      userManager.events.removeUserLoaded(onLoad);
      userManager.events.removeUserUnloaded(onLogout);
      userManager.events.removeSilentRenewError(onSilentRenewFail);
    };
  }, [
    queryClient,
    userManager,
    realmRequiredRoles,
    clientRequiredRoles,
    setActiveClientId,
    config.auth.ignoredClients,
  ]);

  const resolveActiveClientIdFn = useCallback(
    (preferredClientId?: string | null) => {
      if (!user) {
        return null;
      }

      const clientId = resolveActiveClientId(
        user,
        clientRequiredRoles,
        realmRequiredRoles,
        config.auth.ignoredClients,
        preferredClientId,
      );
      return clientId;
    },
    [user, clientRequiredRoles, realmRequiredRoles, config.auth.ignoredClients],
  );

  const authStatusValue: AuthStatusCtx = useMemo(
    () => ({
      status,
      user: user ?? null,
      logout,
    }),
    [status, user, logout],
  );

  const authOperationsValue: AuthOperationsCtx = useMemo(
    () => ({
      canExecuteAdminOperation,
      canExecuteAnyAdminOperation,
      availableAdminOperations,
      clientOperationsByClientId,
    }),
    [
      canExecuteAdminOperation,
      canExecuteAnyAdminOperation,
      availableAdminOperations,
      clientOperationsByClientId,
    ],
  );

  const accessibleClientIds = useMemo(
    () => Array.from(clientRolesByClientId.keys()),
    [clientRolesByClientId],
  );

  const activeClientValue: ActiveClientCtx = useMemo(
    () => ({
      activeClientId,
      setActiveClientId,
      accessibleClientIds,
      resolveActiveClientId: resolveActiveClientIdFn,
    }),
    [
      activeClientId,
      setActiveClientId,
      accessibleClientIds,
      resolveActiveClientIdFn,
    ],
  );

  const queryClientValue: QueryClientCtx = useMemo(
    () => ({
      queryClient,
    }),
    [queryClient],
  );

  const authValue: AuthCtx = useMemo(() => {
    const baseValue = {
      logout,
      queryClient,
      canExecuteAdminOperation,
      canExecuteAnyAdminOperation,
      activeClientId,
      setActiveClientId,
      resolveActiveClientId: resolveActiveClientIdFn,
      accessibleClientIds,
    };

    switch (status) {
      case "authenticated": {
        if (!user) {
          console.error("User unexpectedly null in authenticated state");
          return {
            ...baseValue,
            status: "unauthenticated" as const,
            user: null,
            canExecuteOperation: canExecuteOperationForActiveClient,
          };
        }
        return {
          ...baseValue,
          status: "authenticated" as const,
          user,
          canExecuteOperation: canExecuteOperationForActiveClient,
        };
      }
      case "unauthorized": {
        if (!user) {
          console.error("User unexpectedly null in unauthorized state");
          return {
            ...baseValue,
            status: "unauthenticated" as const,
            user: null,
            canExecuteOperation: () => false,
          };
        }
        return {
          ...baseValue,
          status: "unauthorized" as const,
          user,
          canExecuteAdminOperation: () => false,
          canExecuteOperation: () => false,
          canExecuteAnyAdminOperation: () => false,
        };
      }
      case "loading":
        return {
          ...baseValue,
          status: "loading" as const,
          user: null,
          canExecuteOperation: canExecuteOperationForActiveClient,
        };
      default:
        return {
          ...baseValue,
          status: "unauthenticated" as const,
          user: null,
          canExecuteOperation: canExecuteOperationForActiveClient,
        };
    }
  }, [
    status,
    user,
    logout,
    queryClient,
    canExecuteAdminOperation,
    canExecuteOperationForActiveClient,
    canExecuteAnyAdminOperation,
    activeClientId,
    setActiveClientId,
    resolveActiveClientIdFn,
    accessibleClientIds,
  ]);

  return {
    authStatusValue,
    authOperationsValue,
    activeClientValue,
    queryClientValue,
    authValue,
  };
}

export const useAuth = (): AuthCtx => useContext(AuthContext);

export const useAuthStatus = (): AuthStatusCtx => useContext(AuthStatusContext);

export const useAuthOperations = (): AuthOperationsCtx =>
  useContext(AuthOperationsContext);

export const useActiveClient = (): ActiveClientCtx =>
  useContext(ActiveClientContext);

export const useAuthQueryClient = (): QueryClientCtx => {
  const ctx = useContext(QueryClientContext);
  if (!ctx) {
    throw new Error("useAuthQueryClient must be used within AuthQueryProvider");
  }
  return ctx;
};

// Returns a function to check if the current user can execute a given operation.
//
// NOTE This hook subscribes to AuthStatusContext, AuthOperationsContext, and
// ActiveClientContext. Components using this hook will re-render when ANY of
// these contexts change. For optimal performance in components that only need
// admin operation checks, use `useAuthOperations().canExecuteAdminOperation`
// directly instead.
export function useCanExecuteOperation(): (
  operationId: OperationId,
) => boolean {
  const { status } = useAuthStatus();
  const { canExecuteAdminOperation, clientOperationsByClientId } =
    useAuthOperations();
  const { activeClientId } = useActiveClient();

  return useMemo(
    () =>
      createCanExecuteOperationFn(
        status,
        activeClientId,
        canExecuteAdminOperation,
        clientOperationsByClientId,
      ),
    [
      status,
      activeClientId,
      canExecuteAdminOperation,
      clientOperationsByClientId,
    ],
  );
}
